<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ApiRequestError, register } from '@/api/client'
import { useAuthStore } from '@/stores/auth'
import { RegisterSchema, firstIssue } from '@/validation'
import { maxLength, tidy } from '@/lib/inputFormat'

const router = useRouter()
const auth = useAuthStore()
const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const busy = ref(false)
const error = ref('')
const passwordsMatch = computed(
  () => !confirmPassword.value || password.value === confirmPassword.value,
)

function tidyEmail() {
  email.value = tidy('email', email.value) as string
}

async function submit() {
  error.value = ''
  if (password.value !== confirmPassword.value) {
    error.value = 'Passwords do not match.'
    return
  }
  const issue = firstIssue(
    RegisterSchema.safeParse({ email: email.value, password: password.value }),
  )
  if (issue !== undefined) {
    error.value = issue
    return
  }
  busy.value = true
  try {
    auth.setAccount(await register(email.value, password.value))
    await router.replace('/account')
  } catch (cause) {
    error.value =
      cause instanceof ApiRequestError
        ? cause.message
        : 'Unable to create your account. Please try again.'
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <main class="auth-page">
    <section class="auth-card" aria-labelledby="register-title">
      <p class="eyebrow">Castle Country Digital</p>
      <h1 id="register-title" class="display">Create account</h1>
      <p class="auth-lede">
        Accounts begin with member access. An administrator can grant intake management access.
      </p>
      <form novalidate @submit.prevent="submit">
        <label
          >Email
          <input
            v-model="email"
            type="email"
            autocomplete="email"
            autocapitalize="off"
            spellcheck="false"
            enterkeyhint="next"
            :maxlength="maxLength.text"
            required
            :disabled="busy"
            @blur="tidyEmail"
        /></label>
        <label
          >Password
          <input
            v-model="password"
            type="password"
            autocomplete="new-password"
            minlength="10"
            required
            :disabled="busy"
          /><small>Use 10–128 characters.</small></label
        >
        <label
          >Confirm password
          <input
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            required
            :aria-invalid="!passwordsMatch"
            :disabled="busy"
        /></label>
        <p v-if="error" class="form-error" role="alert">{{ error }}</p>
        <button class="btn btn-primary" type="submit" :disabled="busy">
          {{ busy ? 'Creating account…' : 'Create account' }}
        </button>
      </form>
      <p class="auth-switch">
        Already have an account? <RouterLink to="/login">Sign in</RouterLink>
      </p>
    </section>
  </main>
</template>
